import { PayrollTypeFullEnum } from '@shared-enums/time.enum';
import { IPayrollTimeOffRequestResult } from '@shared-interfaces/api';
import { IDayOfWeek } from '@shared-interfaces/dates';

import { IIterablePayroll } from '../../components/modal/modal-content-time-eval/modal-content-time-eval.component';
import {
  IEmployeePayrollSummaryRow,
  IEquipmentPayrollSummaryRow,
  IProductionQuantityPayrollSummaryRow,
  ISelectOptions,
  ITableConfigEntity,
} from '../../interfaces/internal-interfaces';
import { EmployeeCatsRecord } from '../employeeCatsRecord';
import { EquipmentCatsRecord } from '../EquipmentCatsRecord';
import { NonbillableCatsRecord } from '../NonbillableCatsRecord';
import { IPayrollTotals } from '../PayrollTimesheet';
import { ProductionQuantityCatsRecord } from '../ProductionQuantityCatsRecords';
import { ProductionText } from '../ProductionText';
import { ProductionTextRecord } from '../ProductionTextRecord';
import { makeComplexConfigItem, makeConfigItem, makeDaysConfig, makeTextDaysConfig } from './table-config-helpers';

export const payrollOverviewConfig: ITableConfigEntity<keyof IPayrollTotals>[] = [
  makeComplexConfigItem<IPayrollTotals>('Type', 'type', 'left-text', 2, false, false, {
    headerClass: 'text-left',
  }),
  makeComplexConfigItem<IPayrollTotals>('Standard', PayrollTypeFullEnum.Straight, 'text', 1, false, true),
  makeComplexConfigItem<IPayrollTotals>('Over', PayrollTypeFullEnum.Overtime, 'text', 1, false, true),
  makeComplexConfigItem<IPayrollTotals>('Double', PayrollTypeFullEnum.Double, 'text', 1, false, true),
  makeComplexConfigItem<IPayrollTotals>('Total', 't', 'text', 1, false, true, {
    headerClass: 'text-right',
    class: '!text-right !justify-end',
  }),
];

export function makePayrollEmployeeSummaryConfig(): ITableConfigEntity<keyof IEmployeePayrollSummaryRow>[] {
  const shouldSpanAllRows = true;
  return [
    //NOTE: any changes to the column numbers in here must also be updated in the makePayrollEmployeePerDiemConfig!
    makeComplexConfigItem<IEmployeePayrollSummaryRow>('Employee', 'employee', 'left-text', 8, shouldSpanAllRows, true, {
      shouldDetectClick: true,
      clickValue: 'horizontalBulkEntry',
      shouldShowEntityTooltip: true,
      headerClass: 'text-left pl-[5px]',
    }),
    makeComplexConfigItem<IEmployeePayrollSummaryRow>('Total', 'total', 'bold-text', 3, shouldSpanAllRows, true, {
      shouldDetectClick: true,
      clickValue: 'timeEval',
      class: 'text-center !text-base !font-medium', //this negates the 'bold-text' type but apparently that is the only way to make it be a clickable white box!
      shouldTotal: true,
    }),
    makeComplexConfigItem<IEmployeePayrollSummaryRow>('S', 's', 'text', 2, shouldSpanAllRows, true, {
      tooltip: 'Standard Time',
      shouldTotal: true,
      shouldTextRemoveZerosFromNonTotalRow: true,
      class: '!h-[38px]',
    }),
    makeComplexConfigItem<IEmployeePayrollSummaryRow>('O', 'o', 'text', 2, shouldSpanAllRows, true, {
      tooltip: 'Overtime',
      shouldTotal: true,
      shouldTextRemoveZerosFromNonTotalRow: true,
      class: '!h-[38px]',
    }),
    makeComplexConfigItem<IEmployeePayrollSummaryRow>('D', 'd', 'text', 2, shouldSpanAllRows, true, {
      tooltip: 'Double Time',
      shouldTotal: true,
      shouldTextRemoveZerosFromNonTotalRow: true,
      class: '!h-[38px]',
    }),
    makeComplexConfigItem<IEmployeePayrollSummaryRow>('Paid As', 'activity', 'left-text', 7, false, true, {
      class: 'border-l pl-2 border-gray-300 h-full',
      headerClass: 'text-left pl-3',
    }),
    ...makeDaysConfig('number-grid', 2, true, '', 'Add Paid Per Diem'),
  ];
}

export function makePayrollEmployeePerDiemConfig(): ITableConfigEntity<keyof IEmployeePayrollSummaryRow>[] {
  //this should mostly mirror what columns makePayrollEmployeeSummaryConfig() has,
  //but we don't need header titles and the spacing and css will be different
  const shouldSpanAllRows = false;
  const perDiemSeparatorClasses = 'border-t border-gray-300 mt-1';
  return [
    makeComplexConfigItem<IEmployeePayrollSummaryRow>('', 'employee', 'left-text', 8, shouldSpanAllRows, true, {
      class: `pl-[7px] pt-[3px] h-full ${perDiemSeparatorClasses}`, //7px padding lines it up horizontally with the text in the box on the line above this, and 3px padding lines it up vertically with the one on the same row
    }),
    //We want this item to span the 'total' and the 's' column, so we add those two column values together
    makeComplexConfigItem<IEmployeePayrollSummaryRow>('', 'total', 'left-text', 5, shouldSpanAllRows, true, {
      shouldDetectClick: true,
      clickValue: 'horizontalPerDiemBulkEntry',
      class: `text-center !text-base !font-medium pl-[3px] h-full ${perDiemSeparatorClasses}`, //this negates the 'bold-text' type but apparently that is the only way to make it be a clickable white box!
    }),
    //We just need a spacer column to take up all space from 'o' + 'd' + 'activity' so we add all of those column values together
    makeComplexConfigItem<IEmployeePayrollSummaryRow>('', 'activity', 'text', 11, shouldSpanAllRows, true, {
      class: perDiemSeparatorClasses,
    }),
    ...makeDaysConfig('number-grid', 2, false, `per-diem-day-box pt-1 ${perDiemSeparatorClasses}`),
  ];
}

export const payrollProductionTextWeeklyConfig: ITableConfigEntity<keyof ProductionText | IDayOfWeek>[] = [
  makeComplexConfigItem<ProductionText>('Production Item', 'display', 'bold-text', 1, false, true, { class: '!text-black' }),
  ...makeTextDaysConfig('embedded-text'),
];

export function makePayrollEquipmentWeeklyConfig(): ITableConfigEntity<keyof IEquipmentPayrollSummaryRow>[] {
  return [
    makeComplexConfigItem<IEquipmentPayrollSummaryRow>('Equipment', 'equipment', 'left-text', 6, true, true, {
      shouldDetectClick: true,
      clickValue: 'horizontalBulkEntry',
      headerClass: 'text-left pl-[5px]',
    }),
    makeComplexConfigItem<IEquipmentPayrollSummaryRow>('Total', 'total', 'text', 2, false, true, {
      shouldTotal: true,
      class: 'h-full',
    }),
    ...makeDaysConfig('number-box', 1),
  ];
}

export function makePayrollProductionQuantityWeeklyConfig(): ITableConfigEntity<keyof IProductionQuantityPayrollSummaryRow>[] {
  return [
    makeComplexConfigItem<IProductionQuantityPayrollSummaryRow>('Description', 'display', 'left-text', 4, true, true, {
      headerClass: 'text-left pl-[5px]',
    }),
    makeComplexConfigItem<IProductionQuantityPayrollSummaryRow>('Total', 'total', 'text', 2, false, true, { class: 'h-full' }),
    ...makeDaysConfig('number-box', 1),
  ];
}

export function makePayrollProductionTextDailyConfig(): ITableConfigEntity[] {
  return [
    makeComplexConfigItem<ProductionTextRecord>('Value', 'LongTextstr', 'text-input', 11, false, false, {
      minTextRows: 1,
      maxTextRows: 2,
      shouldRemoveMatStyling: false,
      class: 'pb-5',
      headerClass: '!text-left',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'post_add',
      shouldDetectClick: true,
      clickValue: 'addClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'delete',
      shouldDetectClick: true,
      clickValue: 'deleteClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'info',
      shouldDetectClick: true,
      clickValue: 'showCats',
    }),
  ];
}

export function makePayrollProductionQuantityDailyConfig(isReadonly: boolean): ITableConfigEntity[] {
  return [
    makeComplexConfigItem<ProductionQuantityCatsRecord>('Billed As', 'Zmatnr', isReadonly ? 'text' : 'select', 5, false, false, {
      headerClass: '!text-left',
    }),
    makeComplexConfigItem<ProductionQuantityCatsRecord>('Quantity', 'Quantity', 'input', 2, false, false),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'post_add',
      shouldDetectClick: true,
      clickValue: 'addClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'delete',
      shouldDetectClick: true,
      clickValue: 'deleteClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'info',
      shouldDetectClick: true,
      clickValue: 'showCats',
    }),
  ];
}

export function makePayrollEmployeeDailyConfig(activityTypes: ISelectOptions[]): ITableConfigEntity[] {
  return [
    makeComplexConfigItem<EmployeeCatsRecord>('Paid As', 'Acttype', 'select', 4, false, false, {
      selectOptions: activityTypes,
      headerClass: '!text-left',
    }),
    makeComplexConfigItem<EmployeeCatsRecord>('Billed As', 'Zmatnr', 'select', 4, false, false, { headerClass: '!text-left' }),
    makeComplexConfigItem<EmployeeCatsRecord>('Hours', 'Catshours', 'hours', 3, false, false),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'post_add',
      shouldDetectClick: true,
      clickValue: 'addClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'delete',
      shouldDetectClick: true,
      clickValue: 'deleteClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'info',
      shouldDetectClick: true,
      clickValue: 'showCats',
    }),
  ];
}

export function makePayrollEquipmentDailyConfig(): ITableConfigEntity[] {
  return [
    makeComplexConfigItem<EquipmentCatsRecord>('Billed As', 'Zmatnr', 'select', 6, false, false, { headerClass: '!text-left' }),
    makeComplexConfigItem<EquipmentCatsRecord>('Hours', 'Catshours', 'hours', 3, false, false),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'post_add',
      shouldDetectClick: true,
      clickValue: 'addClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'delete',
      shouldDetectClick: true,
      clickValue: 'deleteClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'info',
      shouldDetectClick: true,
      clickValue: 'showCats',
    }),
  ];
}

export const nonbillableDailyConfig: ITableConfigEntity[] = [
  makeComplexConfigItem<NonbillableCatsRecord>('Nonbillable Code', 'AbsAttType', 'plain-button', 8, false, false, {
    isRequired: true,
    clickValue: 'nonbillableCodeClicked',
    displayKey: '__AbsAttTypeDescription',
    class: 'px-2',
  }),
  makeComplexConfigItem<NonbillableCatsRecord>('Hours', 'Catshours', 'hours', 3, false, false, { isRequired: true }),
  makeComplexConfigItem('', '', 'spacer', 1, false, false),
  makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
    icon: 'delete',
    shouldDetectClick: true,
    clickValue: 'deleteClicked',
  }),
  makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
    icon: 'info',
    shouldDetectClick: true,
    clickValue: 'showCats',
  }),
];

export const perDiemDailyConfig: ITableConfigEntity[] = [
  makeComplexConfigItem('Per Diems', 'PerDiems.label', 'left-text', 7, false, false, { headerClass: 'text-left' }),
  makeComplexConfigItem('', 'Zperdiemamount', 'currency-input', 3, false, false),
  makeComplexConfigItem('', '', 'spacer', 2, false, false),
  makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
    icon: 'info',
    shouldDetectClick: true,
    clickValue: 'showCats',
  }),
];

export const torDailyConfig: ITableConfigEntity[] = [
  makeComplexConfigItem<IPayrollTimeOffRequestResult>('', 'Ktext', 'left-text', 1, false, true),
  makeComplexConfigItem<IPayrollTimeOffRequestResult>('', 'Abshours', 'left-text', 1, false, true),
  makeComplexConfigItem('', '', 'image', 1, true, true, {
    icon: 'tor',
    shouldDetectClick: true,
    clickValue: 'navigateToTor',
    imageHeight: 76,
    imageWidth: 100,
  }),
];

export const timeEvalSummary: ITableConfigEntity[] = [
  makeComplexConfigItem<IIterablePayroll>('Type', 'type', 'left-text', 2, false, true, {
    class: 'font-semibold, ml-4',
    headerClass: '!text-left pl-4',
  }),
  ...makeDaysConfig('text', 1, false),
  makeConfigItem('Total', 'text', 1, false, false),
];
